import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  Animated,
} from 'react-native';
import {
  Text,
  useTheme,
  Portal,
  Modal,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NavigationProp } from '@react-navigation/native';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Eye,
  Send,
  FileText,
  Coffee,
  ListOrdered,
  Settings2,
  Image,
  X,
  Trash2,
} from 'lucide-react-native';
import {
  BrewingMethod,
  RecipeDifficulty,
  RecipeStatus,
  GrindSize,
} from '@coffeeplexity/shared';
import { useCreateRecipe, useUpdateRecipe } from '@coffeeplexity/api-client';
import { RecipeBasicInfo } from '../components/recipes/editor/RecipeBasicInfo';
import { RecipeIngredients } from '../components/recipes/editor/RecipeIngredients';
import { RecipeSteps } from '../components/recipes/editor/RecipeSteps';
import { RecipeEquipment } from '../components/recipes/editor/RecipeEquipment';
import { RecipeImages } from '../components/recipes/editor/RecipeImages';
import { RecipePreview } from '../components/recipes/editor/RecipePreview';
import { useDraftSaving } from '../hooks/useDraftSaving';
import { Toast } from '../utils/toast';
import { CrossPlatformAlert } from '../utils/crossPlatformAlert';
import { logger } from '../utils/logger';

// Helper function to format relative time
const getTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// Validation schema
const recipeSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title too long'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  brewing_method: z.nativeEnum(BrewingMethod),
  difficulty: z.nativeEnum(RecipeDifficulty),
  servings: z.number().min(1).max(20),
  prep_time_minutes: z.number().min(1).max(180),
  total_time_minutes: z.number().min(1).max(480),
  brewing_parameters: z.object({
    grind_size: z.nativeEnum(GrindSize),
    water_temperature_celsius: z.number().min(60).max(100),
    coffee_to_water_ratio: z.string().min(1),
    bloom_time_seconds: z.number().optional(),
    total_brew_time_seconds: z.number().min(30),
    pressure_bars: z.number().optional(),
  }),
  ingredients: z
    .array(
      z.object({
        name: z.string().min(1),
        amount: z.number().positive(),
        unit: z.string().min(1),
        notes: z.string().optional(),
      })
    )
    .min(1, 'At least one ingredient is required'),
  steps: z
    .array(
      z.object({
        step_number: z.number().positive(),
        title: z.string().min(1),
        description: z.string().min(1),
        duration_seconds: z.number().optional(),
        temperature_celsius: z.number().optional(),
        notes: z.string().optional(),
        image_url: z.string().optional(),
      })
    )
    .min(1, 'At least one step is required'),
  equipment_ids: z.array(z.string()),
  images: z.array(z.string()),
  tags: z.array(z.string()),
  video_url: z.string().optional(),
  is_public: z.boolean(),
});

type RecipeFormData = z.infer<typeof recipeSchema>;

interface RouteParams {
  recipeId?: string;
}

// Tab button component with Apple/Nothing-inspired design
interface TabButtonProps {
  icon: React.ComponentType<any>;
  label: string;
  isActive: boolean;
  onPress: () => void;
  isDark: boolean;
  colors: any;
}

const TabButton: React.FC<TabButtonProps> = ({
  icon: Icon,
  label,
  isActive,
  onPress,
  isDark,
  colors,
}) => {
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    Animated.spring(scaleAnim, {
      toValue: 0.95,
      useNativeDriver: true,
      friction: 8,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scaleAnim, {
      toValue: 1,
      useNativeDriver: true,
      friction: 8,
    }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
    >
      <Animated.View
        style={[
          styles.tabButton,
          {
            transform: [{ scale: scaleAnim }],
            backgroundColor: isActive
              ? isDark
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(0, 0, 0, 0.06)'
              : 'transparent',
            borderColor: isActive
              ? isDark
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0, 0, 0, 0.08)'
              : 'transparent',
          },
        ]}
      >
        <Icon
          size={18}
          color={isActive ? colors.primary : colors.onSurfaceVariant}
          strokeWidth={isActive ? 2.5 : 2}
        />
        <Text
          variant="labelMedium"
          style={[
            styles.tabLabel,
            {
              color: isActive ? colors.onSurface : colors.onSurfaceVariant,
              fontWeight: isActive ? '600' : '500',
            },
          ]}
        >
          {label}
        </Text>
      </Animated.View>
    </Pressable>
  );
};

// Action button with premium feel
interface ActionButtonProps {
  icon: React.ComponentType<any>;
  label: string;
  onPress: () => void;
  variant: 'primary' | 'secondary' | 'tertiary';
  disabled?: boolean;
  loading?: boolean;
  colors: any;
  isDark: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  icon: Icon,
  label,
  onPress,
  variant,
  disabled,
  loading,
  colors,
  isDark,
}) => {
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    if (!disabled && !loading) {
      Animated.spring(scaleAnim, {
        toValue: 0.96,
        useNativeDriver: true,
        friction: 8,
      }).start();
    }
  };

  const handlePressOut = () => {
    Animated.spring(scaleAnim, {
      toValue: 1,
      useNativeDriver: true,
      friction: 8,
    }).start();
  };

  const getStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: colors.primary,
          textColor: colors.onPrimary,
          borderColor: 'transparent',
        };
      case 'secondary':
        return {
          backgroundColor: isDark
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.04)',
          textColor: colors.onSurface,
          borderColor: isDark
            ? 'rgba(255, 255, 255, 0.15)'
            : 'rgba(0, 0, 0, 0.08)',
        };
      case 'tertiary':
        return {
          backgroundColor: 'transparent',
          textColor: colors.primary,
          borderColor: colors.primary,
        };
      default:
        return {
          backgroundColor: colors.surface,
          textColor: colors.onSurface,
          borderColor: colors.outline,
        };
    }
  };

  const buttonStyles = getStyles();

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      disabled={disabled || loading}
    >
      <Animated.View
        style={[
          styles.actionButton,
          {
            transform: [{ scale: scaleAnim }],
            backgroundColor: buttonStyles.backgroundColor,
            borderColor: buttonStyles.borderColor,
            opacity: disabled ? 0.5 : 1,
          },
        ]}
      >
        <Icon
          size={16}
          color={buttonStyles.textColor}
          strokeWidth={2}
        />
        <Text
          variant="labelMedium"
          style={[
            styles.actionButtonLabel,
            { color: buttonStyles.textColor },
          ]}
        >
          {label}
        </Text>
      </Animated.View>
    </Pressable>
  );
};

export function RecipeCreateScreen() {
  const theme = useTheme();
  const colors = theme.colors;
  const isDark = theme.dark;
  const navigation = useNavigation();
  const route = useRoute();
  const { recipeId } = (route.params as RouteParams) || {};

  const [activeTab, setActiveTab] = useState('basic');
  const [showPreview, setShowPreview] = useState(false);
  const [, setIsDraft] = useState(true);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(20)).current;

  // Ref to track latest form data for debounced saving
  const formDataRef = useRef<RecipeFormData | undefined>(undefined);

  const createRecipe = useCreateRecipe();
  const updateRecipe = useUpdateRecipe();

  // Entry animation
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  // Initialize form with default values
  const defaultValues: RecipeFormData = {
    title: '',
    description: '',
    brewing_method: BrewingMethod.POUR_OVER,
    difficulty: RecipeDifficulty.BEGINNER,
    servings: 1,
    prep_time_minutes: 5,
    total_time_minutes: 10,
    brewing_parameters: {
      grind_size: GrindSize.MEDIUM,
      water_temperature_celsius: 85,
      coffee_to_water_ratio: '1:15',
      total_brew_time_seconds: 240,
    },
    ingredients: [
      {
        name: 'Coffee beans',
        amount: 20,
        unit: 'g',
        notes: '',
      },
      {
        name: 'Water',
        amount: 300,
        unit: 'ml',
        notes: '',
      },
    ],
    steps: [
      {
        step_number: 1,
        title: 'Prepare equipment',
        description:
          'Set up your brewing equipment and heat water to the desired temperature.',
        duration_seconds: 60,
      },
    ],
    equipment_ids: [],
    images: [],
    tags: [],
    is_public: false,
  };

  const methods = useForm<RecipeFormData>({
    resolver: zodResolver(recipeSchema),
    defaultValues,
  });

  const {
    watch,
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;
  const formData = watch();

  // Draft saving functionality
  const draftKey = recipeId ? `recipe-draft-${recipeId}` : 'recipe-draft-new';
  const { saveDraft, loadDraft, clearDraft } = useDraftSaving(draftKey);

  // Update ref with latest form data
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (!isDirty) return;

    const interval = setInterval(() => {
      if (formDataRef.current) {
        saveDraft(formDataRef.current);
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [isDirty, saveDraft]);

  // Save draft on form data change (debounced)
  useEffect(() => {
    if (!isDirty) return;

    const timeoutId = setTimeout(() => {
      if (formDataRef.current) {
        saveDraft(formDataRef.current);
        setLastSavedAt(new Date());
      }
    }, 2000); // 2 seconds after last change

    return () => clearTimeout(timeoutId);
  }, [isDirty, saveDraft]);

  // Load draft on mount if creating new recipe
  useEffect(() => {
    if (!recipeId) {
      const loadDraftData = async () => {
        const draft = await loadDraft();
        if (draft) {
          methods.reset(draft);
          setIsDraft(true);
        }
      };
      loadDraftData();
    }
  }, [recipeId, loadDraft, methods]);

  const onSubmit = useCallback(
    async (data: RecipeFormData, status: RecipeStatus = RecipeStatus.DRAFT) => {
      try {
        const recipeData = {
          ...data,
          status,
        };

        if (recipeId) {
          await updateRecipe.mutateAsync({
            id: recipeId,
            recipe: recipeData,
          });
          Toast.show('Recipe updated successfully!', 'success');
        } else {
          const newRecipe = await createRecipe.mutateAsync(recipeData);
          clearDraft();
          Toast.show('Recipe created successfully!', 'success');
          (navigation as NavigationProp<any>).navigate('recipes', {
            screen: 'recipeDetail',
            params: { recipeId: newRecipe.id },
          });
        }
      } catch (error) {
        logger.error('Error saving recipe:', error);
        Toast.show('Failed to save recipe. Please try again.', 'error');
      }
    },
    [recipeId, updateRecipe, createRecipe, clearDraft, navigation]
  );

  const handleSaveDraft = () => {
    handleSubmit(data => onSubmit(data, RecipeStatus.DRAFT))();
  };

  const handlePublish = () => {
    handleSubmit(data => onSubmit(data, RecipeStatus.PUBLISHED))();
  };

  const handleDiscard = useCallback(() => {
    console.log('disacrd')
    CrossPlatformAlert.alert(
      'Discard Changes',
      'Are you sure you want to discard all unsaved changes? This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Discard',
          style: 'destructive',
          onPress: () => {
            methods.reset(defaultValues);
            clearDraft();
            Toast.show('Changes discarded', 'info');
          },
        },
      ]
    );
  }, [methods, clearDraft]);

  // Note: Back navigation is handled by the navigator header, but we keep
  // the unsaved changes logic available for programmatic use if needed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const confirmDiscardChanges = useCallback(() => {
    if (isDirty) {
      CrossPlatformAlert.alert(
        'Unsaved Changes',
        'You have unsaved changes. Do you want to save as draft before leaving?',
        [
          {
            text: 'Discard',
            style: 'destructive',
            onPress: () => navigation.goBack(),
          },
          { text: 'Save Draft', onPress: handleSaveDraft },
          { text: 'Cancel', style: 'cancel' },
        ]
      );
      return false;
    }
    return true;
  }, [isDirty, navigation, handleSaveDraft]);

  const tabs = [
    { value: 'basic', label: 'Details', icon: FileText },
    { value: 'ingredients', label: 'Ingredients', icon: Coffee },
    { value: 'steps', label: 'Steps', icon: ListOrdered },
    { value: 'equipment', label: 'Equipment', icon: Settings2 },
    { value: 'images', label: 'Media', icon: Image },
  ];

  const isLoading = createRecipe.isPending || updateRecipe.isPending;

  return (
    <FormProvider {...methods}>
      <SafeAreaView
        style={[styles.container, { backgroundColor: colors.background }]}
      >
        <KeyboardAvoidingView
          style={styles.keyboardAvoid}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <Animated.View
            style={[
              styles.animatedContainer,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            {/* Header - Apple-inspired minimalist design */}
            <View
              style={[
                styles.header,
                {
                  backgroundColor: isDark
                    ? 'rgba(28, 27, 31, 0.95)'
                    : 'rgba(255, 255, 255, 0.95)',
                  borderBottomColor: isDark
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.06)',
                },
              ]}
            >
              <View style={styles.headerTop}>
                <Text
                  variant="headlineSmall"
                  style={[
                    styles.headerTitle,
                    { color: colors.onSurface },
                  ]}
                >
                  {recipeId ? 'Edit Recipe' : 'New Recipe'}
                </Text>

                <View style={styles.headerActions}>
                  <ActionButton
                    icon={Eye}
                    label="Preview"
                    onPress={() => setShowPreview(true)}
                    variant="secondary"
                    disabled={isLoading}
                    colors={colors}
                    isDark={isDark}
                  />
                  <ActionButton
                    icon={Trash2}
                    label="Discard"
                    onPress={handleDiscard}
                    variant="tertiary"
                    disabled={isLoading}
                    colors={colors}
                    isDark={isDark}
                  />
                  <ActionButton
                    icon={Send}
                    label="Publish"
                    onPress={handlePublish}
                    variant="primary"
                    disabled={isLoading}
                    loading={isLoading}
                    colors={colors}
                    isDark={isDark}
                  />
                </View>
              </View>

              {/* Status row - full width */}
              <View style={styles.statusRow}>
                <View
                  style={[
                    styles.statusBadge,
                    {
                      backgroundColor: isDirty
                        ? isDark
                          ? 'rgba(255, 183, 77, 0.15)'
                          : 'rgba(255, 152, 0, 0.12)'
                        : isDark
                        ? 'rgba(129, 199, 132, 0.15)'
                        : 'rgba(76, 175, 80, 0.12)',
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.statusDot,
                      {
                        backgroundColor: isDirty
                          ? '#FFB74D'
                          : '#81C784',
                      },
                    ]}
                  />
                  <Text
                    variant="labelSmall"
                    style={[
                      styles.statusText,
                      {
                        color: isDirty
                          ? isDark
                            ? '#FFB74D'
                            : '#E65100'
                          : isDark
                          ? '#81C784'
                          : '#2E7D32',
                      },
                    ]}
                  >
                    {isDirty ? 'Editing' : 'Saved'}
                  </Text>
                </View>

                {/* Last saved timestamp */}
                {lastSavedAt && (
                  <Text
                    variant="labelSmall"
                    style={[
                      styles.lastSavedText,
                      { color: colors.onSurfaceVariant },
                    ]}
                  >
                    Last saved {getTimeAgo(lastSavedAt)}
                  </Text>
                )}
              </View>
            </View>

            {/* Tab Navigation - Nothing-inspired clean design */}
            <View
              style={[
                styles.tabContainer,
                {
                  backgroundColor: isDark
                    ? 'rgba(28, 27, 31, 0.6)'
                    : 'rgba(255, 255, 255, 0.8)',
                  borderBottomColor: isDark
                    ? 'rgba(255, 255, 255, 0.06)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
              ]}
            >
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.tabScrollContent}
              >
                {tabs.map(tab => (
                  <TabButton
                    key={tab.value}
                    icon={tab.icon}
                    label={tab.label}
                    isActive={activeTab === tab.value}
                    onPress={() => setActiveTab(tab.value)}
                    isDark={isDark}
                    colors={colors}
                  />
                ))}
              </ScrollView>
            </View>

            {/* Content Area */}
            <ScrollView
              style={styles.content}
              contentContainerStyle={styles.contentContainer}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.formSection}>
                {activeTab === 'basic' && <RecipeBasicInfo />}
                {activeTab === 'ingredients' && <RecipeIngredients />}
                {activeTab === 'steps' && <RecipeSteps />}
                {activeTab === 'equipment' && <RecipeEquipment />}
                {activeTab === 'images' && <RecipeImages />}
              </View>

              {/* Form Errors - Premium error card design */}
              {Object.keys(errors).length > 0 && (
                <View
                  style={[
                    styles.errorCard,
                    {
                      backgroundColor: isDark
                        ? 'rgba(244, 67, 54, 0.12)'
                        : 'rgba(244, 67, 54, 0.08)',
                      borderColor: isDark
                        ? 'rgba(244, 67, 54, 0.3)'
                        : 'rgba(244, 67, 54, 0.2)',
                    },
                  ]}
                >
                  <View style={styles.errorHeader}>
                    <View
                      style={[
                        styles.errorIconContainer,
                        {
                          backgroundColor: isDark
                            ? 'rgba(244, 67, 54, 0.2)'
                            : 'rgba(244, 67, 54, 0.15)',
                        },
                      ]}
                    >
                      <X size={14} color="#F44336" strokeWidth={2.5} />
                    </View>
                    <Text
                      variant="titleSmall"
                      style={[
                        styles.errorTitle,
                        { color: isDark ? '#EF9A9A' : '#C62828' },
                      ]}
                    >
                      Please fix the following
                    </Text>
                  </View>
                  <View style={styles.errorList}>
                    {Object.entries(errors).map(([field, error]) => (
                      <Text
                        key={field}
                        variant="bodySmall"
                        style={[
                          styles.errorItem,
                          { color: isDark ? '#FFCDD2' : '#B71C1C' },
                        ]}
                      >
                        • {field.replace(/_/g, ' ')}: {error?.message}
                      </Text>
                    ))}
                  </View>
                </View>
              )}

              {/* Bottom spacing */}
              <View style={styles.bottomSpacer} />
            </ScrollView>
          </Animated.View>

          {/* Preview Modal - Apple-inspired modal design */}
          <Portal>
            <Modal
              visible={showPreview}
              onDismiss={() => setShowPreview(false)}
              contentContainerStyle={[
                styles.previewModal,
                {
                  backgroundColor: colors.surface,
                },
              ]}
            >
              <View
                style={[
                  styles.previewHeader,
                  {
                    borderBottomColor: isDark
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.06)',
                  },
                ]}
              >
                <View style={styles.previewHeaderContent}>
                  <Eye size={20} color={colors.primary} strokeWidth={2} />
                  <Text
                    variant="titleMedium"
                    style={[
                      styles.previewTitle,
                      { color: colors.onSurface },
                    ]}
                  >
                    Recipe Preview
                  </Text>
                </View>
                <Pressable
                  onPress={() => setShowPreview(false)}
                  style={[
                    styles.closeButton,
                    {
                      backgroundColor: isDark
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(0, 0, 0, 0.04)',
                    },
                  ]}
                >
                  <X size={18} color={colors.onSurfaceVariant} strokeWidth={2} />
                </Pressable>
              </View>
              <ScrollView
                style={styles.previewContent}
                contentContainerStyle={styles.previewContentContainer}
              >
                <RecipePreview data={formData} />
              </ScrollView>
            </Modal>
          </Portal>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </FormProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoid: {
    flex: 1,
  },
  animatedContainer: {
    flex: 1,
  },

  // Header styles - Apple-inspired
  header: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 12,
    gap: 12,
  },
  headerTitle: {
    fontWeight: '600',
    letterSpacing: -0.5,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    gap: 6,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  lastSavedText: {
    fontSize: 12,
    letterSpacing: 0.1,
    opacity: 0.8,
  },
  discardButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 10,
    gap: 4,
  },
  discardButtonText: {
    fontWeight: '600',
    fontSize: 11,
    letterSpacing: 0.2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  // Tab styles - Nothing-inspired
  tabContainer: {
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  tabScrollContent: {
    paddingHorizontal: 20,
    gap: 6,
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    gap: 8,
  },
  tabLabel: {
    letterSpacing: 0.2,
  },

  // Action button styles
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    gap: 6,
  },
  actionButtonLabel: {
    fontWeight: '600',
    letterSpacing: 0.2,
  },

  // Content styles
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 40,
  },
  formSection: {
    paddingHorizontal: 20,
    paddingTop: 20,
  },

  // Error card styles - Premium design
  errorCard: {
    marginHorizontal: 20,
    marginTop: 20,
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
  },
  errorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  errorIconContainer: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorTitle: {
    fontWeight: '600',
    letterSpacing: -0.2,
  },
  errorList: {
    gap: 6,
  },
  errorItem: {
    lineHeight: 18,
    paddingLeft: 4,
  },

  // Bottom spacer
  bottomSpacer: {
    height: 40,
  },

  // Preview modal styles - Apple-inspired
  previewModal: {
    margin: 16,
    borderRadius: 20,
    maxHeight: '92%',
    overflow: 'hidden',
  },
  previewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  previewHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  previewTitle: {
    fontWeight: '600',
    letterSpacing: -0.3,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewContent: {
    flex: 1,
  },
  previewContentContainer: {
    paddingVertical: 20,
  },
});
